import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { Checkbox, FormControlLabel, Grid, Switch, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import {
  ClientGroupSourceFragment,
  ContentApproval,
  GroupSourceType,
  SourceStatus,
  UpdateGroupSourceMutation,
  UpdateGroupInput,
  useUpdateGroupSourceMutation,
} from 'api';
import { PaddedPaper } from 'components';
import { useNotify } from 'hooks';
import { useUpdateSource } from 'features/groups/updaters';

import SourceDelete from './SourceDelete';

interface IProps {
  source: ClientGroupSourceFragment;
}

const useStyles = makeStyles(theme => ({
  checkboxColor: {
    color: theme.palette.grey[700],
    '&$checked': {
      color: theme.palette.grey[700],
    },
  },
  checked: {},
}));

const Source: FC<IProps> = ({ source }) => {
  const notify = useNotify();
  const classes = useStyles();
  const { t } = useTranslation();
  const { values } = useFormikContext<UpdateGroupInput>();

  const { id, source_status, source_value, source_type, approval } = source;
  const isActive = source_status === SourceStatus.Active;
  const isAutoApprove = approval === ContentApproval.Autoapproved;

  const updateSourceMutationOptions = useUpdateSource(values.id);
  const { mutate } = useUpdateGroupSourceMutation({
    onSuccess: handleSuccess,
    ...updateSourceMutationOptions,
  });

  function handleSuccess({ clientGroupSourceUpdate }: UpdateGroupSourceMutation) {
    const { source_status: nextStatus, approval: nextApproval } = clientGroupSourceUpdate;

    let message = '';

    if (nextStatus !== source_status) {
      message = t('groups:sourceStatusUpdate', {
        value: source_value,
        status: nextStatus,
      });
    }

    if (nextApproval !== approval) {
      const isNextAutoApprove = nextApproval === ContentApproval.Autoapproved;
      const statusMessage = isNextAutoApprove ? t('common:enabled') : t('common:disabled');

      message = t('groups:autoApproveUpdate', {
        value: source_value,
        status: statusMessage,
      });
    }

    notify.info({ message });
  }

  function onChangeSwitch() {
    const status = isActive ? SourceStatus.Disabled : SourceStatus.Active;

    mutate({
      data: {
        id,
        source_status: status,
      },
    });
  }

  function onChangeCheckbox() {
    const approvalOption = isAutoApprove ? ContentApproval.Submitted : ContentApproval.Autoapproved;

    mutate({
      data: {
        id,
        approval: approvalOption,
      },
    });
  }

  function renderValue() {
    if (source_type === GroupSourceType.Keyword) {
      return (
        <Typography variant="subtitle2">{`${t('groups:keyword')}: ${source_value}`}</Typography>
      );
    }

    return <Typography variant="subtitle2">{source_value}</Typography>;
  }

  return (
    <Grid container item spacing={2} alignItems="center">
      <Grid item xs={11}>
        <PaddedPaper>
          <Grid container alignItems="center">
            <Grid container item xs={11} alignItems="center">
              <Grid item xs>
                {renderValue()}
              </Grid>
              <Grid item>
                <Switch color="default" checked={isActive} onChange={onChangeSwitch} />
              </Grid>
            </Grid>
            <Grid container item xs={11}>
              {isActive && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isAutoApprove}
                        onChange={onChangeCheckbox}
                        classes={{ root: classes.checkboxColor, checked: classes.checked }}
                      />
                    }
                    label={t('groups:autoApprove')}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        </PaddedPaper>
      </Grid>
      <Grid item xs={1}>
        <SourceDelete source={source} />
      </Grid>
    </Grid>
  );
};

export default Source;
